import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  type ReactNode,
} from "react";

export interface DesktopGridItem {
  id: string;
  type: "icon" | "widget" | "folder";
  row: number;
  col: number;
  rows: number;
  cols: number;
}

export interface DesktopGridState {
  offsetLeft: number;
  offsetTop: number;
  cellWidth: number;
  cellHeight: number;
  cols: number;
  rows: number;
  paddingX: number;
  paddingY: number;
  items: DesktopGridItem[];
  occupiedCells: Set<string>;
}

interface DesktopGridContextValue {
  gridState: DesktopGridState | null;
  updateGridState: (next: DesktopGridState | null) => void;
}

const DesktopGridContext = createContext<DesktopGridContextValue | null>(null);

export function DesktopGridProvider({ children }: { children: ReactNode }) {
  const [gridState, setGridState] = useState<DesktopGridState | null>(null);

  const updateGridState = useCallback((next: DesktopGridState | null) => {
    setGridState(next);
  }, []);

  const value = useMemo<DesktopGridContextValue>(
    () => ({ gridState, updateGridState }),
    [gridState, updateGridState]
  );

  return (
    <DesktopGridContext.Provider value={value}>
      {children}
    </DesktopGridContext.Provider>
  );
}

export function useDesktopGrid() {
  const ctx = useContext(DesktopGridContext);
  if (!ctx) {
    throw new Error("useDesktopGrid must be used within DesktopGridProvider");
  }
  return ctx;
}
